import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';

import { Title } from '../../../utils/title';
import Icon from '../../../components/Icon';
import { api, useAPIContext } from '../../../api/context';
import dialogs from '../../../dialogs';
import currency from '../../../utils/currency';
import toast from '../../../utils/toast';
import wizard from '../../../utils/wizard';
import Layout from './Layout';
import Campaign from './items/Campaign';
import styles from './Home.module.scss';

const Home = () => {

  const { token } = useAPIContext();

  const { dialog } = dialogs.utils.useDialog();

  const navigate = useNavigate();

  const { ready: wizardReady, missing: wizardMissing } = wizard.useWizard(token);
  const wizardStep = (
    wizardReady && wizardMissing.length > 0
    ? wizard.stepFor(wizardMissing[0])
    : null
  );

  /*
   * After a successful login, route incomplete profiles to the
   * wizard. This only fires once per login — subsequent visits to
   * /campaigns show the inline completion buttons instead.
   */

  useEffect(() => {

    if (!wizardReady) {
      return;
    }

    if (wizardMissing.length === 0) {
      wizard.clearSignup();
    }

    if (!wizard.consumePostLogin()) {
      return;
    }

    if (wizardMissing.length > 0) {
      navigate(wizard.stepFor(wizardMissing[0]).path);
    }

  }, [wizardReady, wizardMissing, navigate]);

  /*
   * profile
   */

  const [ profileLoading, setProfileLoading ] = useState(true);
  const [ profile, setProfile ] = useState();

  useEffect(() => {

    api(token).v1.profile.query()
      .then(res => {
        setProfileLoading(false);
        setProfile(res.body.data);
      })
      .catch(err => {
        setProfileLoading(false);
        toast.show(toast.KIND.WARNING, err);
      });

  }, [token]);

  /*
   * takes availability
   */

  const [ takesAvailable, setTakesAvailable ] = useState(false);

  useEffect(() => {

    api(token).v1.products.feed.available.query()
      .then(res => {
        setTakesAvailable(res.body.available);
      })
      .catch(() => {
        setTakesAvailable(false);
      });

  }, [token]);

  /*
   * items
   */

  const [ itemsLoading, setItemsLoading ] = useState(true);
  const [ items, setItems ] = useState([]);

  useEffect(() => {

    setItemsLoading(true);
    setItems([]);

    api(token).v1.campaigns.query()
      .then(res => {
        setItemsLoading(false);
        setItems(res.body.items);
      })
      .catch(err => {
        setItemsLoading(false);
        toast.show(toast.KIND.WARNING, err);
      });

  }, [token]);

  return (
    <>
      <Title value='Campaigns' />

      {
        dialog === 'points'
        ? <dialogs.campaigns.status.Points />
        : null
      }

      {
        dialog === 'wallet'
        ? <dialogs.campaigns.status.Wallet />
        : null
      }

      {
        dialog === 'paypal'
        ? <dialogs.campaigns.status.PayPal />
        : null
      }

      <Layout>
        <div className={styles.root}>
          <div className={styles.content}>
            <div className={`${styles.status} ${profileLoading ? styles.loading : ''}`}>
              <Link
                className={styles.points}
                to='?dialog=points'
                aria-label='points'>

                <Icon name='database' />

                <span>
                  {
                    profileLoading
                    ? '...'
                    : (
                      profile && `
                        ${profile.claim.points}
                        ${profile.claim.points === 1 ? 'point' : 'points'}
                      `
                    )
                  }
                </span>
              </Link>

              <Link
                className={styles.wallet}
                to='?dialog=wallet'
                aria-label='wallet'>

                <Icon name='wallet' />

                <span>
                  {
                    profileLoading
                    ? '...'
                    : (
                      profile &&
                      `
                        ${currency.repr(profile.wallet.main.currency)}
                        ${profile.wallet.main.balance}
                      `
                    )
                  }
                </span>
              </Link>

              {
                takesAvailable && (
                  <Link
                    className={styles.takes}
                    to='/takes'
                    aria-label='takes'>

                    <Icon name='star' />

                    <span>Takes</span>
                  </Link>
                )
              }
            </div>

            <div className={styles.items}>
              {
                itemsLoading && (
                  <div className={styles.loading}>
                    Loading...
                  </div>
                )
              }

              {
                !itemsLoading &&
                items &&
                items.filter(item => item.user.enable || item.user.active).length === 0 && (
                  <div className={styles.empty}>
                    <p><strong>No campaigns yet — but you're in the right place 👀</strong></p>
                    <p>We're matching you with upcoming campaigns based on your profile.</p>
                    <p>As soon as you qualify, you'll see them here and get notified via email/SMS 🎁</p>
                    <p>👉 Stay tuned — new opportunities drop frequently!</p>
                  </div>
                )
              }

              {
                !itemsLoading &&
                wizardStep &&
                items &&
                items.filter(item => item.user.enable).length === 0 && (
                  <div className={styles.complete}>
                    <Link
                      to={wizardStep.path}
                      aria-label={wizardStep.button.toLowerCase()}>

                      {wizardStep.button}

                    </Link>
                  </div>
                )
              }

              {
                items &&
                items.length > 0 &&
                items
                .filter(item => (
                  item.user.enable ||
                  item.user.active
                ))
                .map(item => (
                  <Campaign
                    key={item.id}
                    campaign={item}
                    wizardStep={wizardStep}
                  />
                ))
              }
            </div>

            <div
              className={styles.footer}
              role='contentinfo'>

              <ul>
                <li>
                  <Link to='https://forms.gle/xhakgu9kQnb283pf6'>
                    Help Center
                  </Link>
                </li>

                <li>
                  <Link to='/campaign-terms-conditions'>
                    Campaign Terms and Conditions
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default Home;